import { DatabaseZap, BookOpen, Sparkles, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface SourceBadgeProps {
  source?: string | null;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Source badges encode *provenance* (where an answer came from), which is a
 * different axis from the *confidence* tier shown by `TierBadge`. To avoid the
 * two systems colliding on color — the old config reused amber (`llm`) and blue
 * (`knowledge_base`), the exact hues the Suggested and Generated tiers use —
 * provenance is now distinguished by its icon + label on a neutral container.
 * Only `vault_data` keeps a color accent: live vault grounding is the
 * product's differentiator and is worth pulling the eye toward. Each entry
 * carries `dark:` variants so it reads correctly on dark cards.
 */
const SOURCE_CONFIG = {
  vault_data: {
    label: 'Live Data',
    Icon: DatabaseZap,
    classes:
      'bg-emerald-50 text-emerald-700 border-emerald-200 ' +
      'dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900',
  },
  knowledge_base: {
    label: 'KB Match',
    Icon: BookOpen,
    classes: 'bg-muted text-muted-foreground border-border',
  },
  llm: {
    label: 'AI Generated',
    Icon: Sparkles,
    classes: 'bg-muted text-muted-foreground border-border',
  },
  firm_required: {
    label: 'Needs Input',
    Icon: AlertCircle,
    classes: 'bg-muted text-muted-foreground border-border',
  },
} as const;

const SIZE_CLASSES = {
  sm: 'text-xs px-1.5 py-0.5',
  md: 'text-sm px-2 py-0.5',
} as const;

const ICON_SIZES = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
} as const;

export function SourceBadge({ source, size = 'md', className }: SourceBadgeProps) {
  if (!source || !(source in SOURCE_CONFIG)) {
    return null;
  }

  const config = SOURCE_CONFIG[source as keyof typeof SOURCE_CONFIG];
  const { label, Icon, classes } = config;

  return (
    <Badge
      variant="outline"
      className={cn(SIZE_CLASSES[size], classes, className)}
      aria-label={`Answer source: ${label}`}
    >
      <Icon className={cn(ICON_SIZES[size], 'mr-1')} aria-hidden="true" />
      {label}
    </Badge>
  );
}
